import { getAlert } from '@hakimo-ui/hakimo/feature-shared';
import { Alarm, AlarmMedia, DataVersion } from '@hakimo-ui/hakimo/types';
import { QueryResult } from '@hakimo-ui/hakimo/ui-elements';
import { isUnifiedDataVersion } from '@hakimo-ui/hakimo/util';
import { UseQueryResult } from 'react-query';
import AlarmFrames from './alarm-frames/AlarmFrames';
import AlarmImage from './alarm-image/AlarmImage';
import AlarmVideo from './alarm-video/AlarmVideo';

interface Props {
  alarm: Alarm;
  queryResult: UseQueryResult<AlarmMedia, Error>;
  onErrorLoadingVideo: () => void;
  disableDownload?: boolean;
  dataVersion?: DataVersion;
}

/**
 * Picks the player for a raw alarm's media. Frames decide it: media is either
 * scan-style frames or an RG video/image URL, never a mix, so frames present
 * means frames rendered.
 */
export function AlarmMediaPlayer(props: Props) {
  const {
    alarm,
    queryResult,
    onErrorLoadingVideo,
    disableDownload,
    dataVersion,
  } = props;

  return (
    <QueryResult queryResult={queryResult}>
      {(data) =>
        data.media?.frames?.length ? (
          <AlarmFrames
            frames={data.media.frames}
            timeZone={alarm.location?.timezone ?? 'UTC'}
            showMatchedFaces={
              isUnifiedDataVersion(dataVersion) ||
              alarm.sourceSystem?.toLowerCase() === 'vision'
            }
            disableDownload={disableDownload}
            showTimeline
          />
        ) : data.videoPath ? (
          <AlarmVideo
            videoPath={data.videoPath}
            videoDetails={data.videoDetails}
            onErrorLoadingVideo={onErrorLoadingVideo}
            matchedFaces={alarm.matchedFaces}
            cameraName={alarm.sourceEntity?.name ?? alarm.camera}
            alarmType={alarm.type}
            disableDownload={disableDownload}
          />
        ) : data.imagePath ? (
          <AlarmImage
            imagePath={data.imagePath}
            disableDownload={disableDownload}
          />
        ) : (
          getAlert('Media not available')
        )
      }
    </QueryResult>
  );
}

export default AlarmMediaPlayer;
